// Parent Communication Hook
// VAN-021: Hook for sending messages from iframe to parent window

import { useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/core/store";
import {
  createIframeMessage,
  createParentMessage,
  IframeMessage,
  MessageType,
  CloseRequestData,
  ThemeChangeData,
  AppReadyData,
} from "@/types/iframe-messages";

/**
 * Hook for sending messages from iframe to parent window
 * Includes safety checks and logging for debugging
 */
export const useParentCommunication = () => {
  const { source, isIframe, parentReady } = useSelector(
    (state: RootState) => state.app
  );

  // Check if we're in an iframe environment
  const isInIframe = useCallback((): boolean => {
    return window.parent !== window && isIframe;
  }, [isIframe]);

  // Send a message to the parent window
  const sendMessage = useCallback(
    <T extends MessageType>(type: T, payload?: any): boolean => {
      if (!isInIframe()) {
        console.log(
          "useParentCommunication: Not in iframe mode, message not sent:",
          type
        );
        return false;
      }

      try {
        const message = createIframeMessage(type, source, payload);
        console.log(
          "useParentCommunication: Sending message to parent:",
          message
        );
        window.parent.postMessage(message, "*");
        return true;
      } catch (error) {
        console.error("useParentCommunication: Error sending message:", error);
        return false;
      }
    },
    [isInIframe, source]
  );

  // Send app ready message with capabilities
  const sendAppReady = useCallback(
    (data: AppReadyData): boolean => {
      return sendMessage("PREORDER_APP_READY", data);
    },
    [sendMessage]
  );

  // Request parent to close the iframe
  const requestClose = useCallback(
    (reason: CloseRequestData["reason"], message?: string): boolean => {
      const closeData: CloseRequestData = {
        reason,
        message,
      };
      return sendMessage("REQUEST_CLOSE", closeData);
    },
    [sendMessage]
  );

  // Send theme change notification
  const sendThemeChange = useCallback(
    (themeData: ThemeChangeData): boolean => {
      return sendMessage("THEME_CHANGED", themeData);
    },
    [sendMessage]
  );

  // Send cancellation message
  const sendCancellation = useCallback((): boolean => {
    return sendMessage("PREORDER_CANCELLED");
  }, [sendMessage]);

  // Send error message
  const sendError = useCallback(
    (error: string, code?: string, details?: any): boolean => {
      return sendMessage("PREORDER_ERROR", {
        error,
        code,
        details,
      });
    },
    [sendMessage]
  );

  // Wait for parent to be ready (useful for initialization)
  const waitForParent = useCallback(
    (timeout: number = 5000): Promise<boolean> => {
      return new Promise((resolve) => {
        if (parentReady) {
          resolve(true);
          return;
        }

        const startTime = Date.now();
        const checkInterval = setInterval(() => {
          if (parentReady || Date.now() - startTime > timeout) {
            clearInterval(checkInterval);
            resolve(parentReady);
          }
        }, 100);
      });
    },
    [parentReady]
  );

  return {
    // State
    isInIframe: isInIframe(),
    parentReady,
    source,

    // Message sending functions
    sendMessage,
    sendAppReady,
    requestClose,
    sendThemeChange,
    sendCancellation,
    sendError,

    // Utilities
    waitForParent,
  };
};
